import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import type { SimulateDataset } from "./simulate.schema";
import type {
  SimulateConversationResult,
  SimulateTurnResult,
} from "./simulate.report";

export type SimulateRunPhase = "need-thread" | "playing" | "done";

/**
 * A run plays a snapshot of the loaded datasets, one dataset per fresh thread,
 * back to back for the cleanest isolation. `datasetIndex` points at the dataset
 * being played; the controller creates a thread for it while `phase` is
 * "need-thread" and the driver plays its turns while "playing".
 */
export type SimulateRun = {
  datasets: SimulateDataset[];
  datasetIndex: number;
  activeThreadId: string | null;
  phase: SimulateRunPhase;
  conversations: SimulateConversationResult[];
};

type SimulateContextValue = {
  datasets: SimulateDataset[];
  addDatasets: (datasets: SimulateDataset[]) => void;
  removeDataset: (index: number) => void;
  clearDatasets: () => void;

  open: boolean;
  setOpen: (open: boolean) => void;

  run: SimulateRun | null;
  startRun: () => void;
  beginConversation: (threadId: string, name?: string) => void;
  recordTurn: (result: SimulateTurnResult) => void;
  finishConversation: () => void;
  reset: () => void;
};

const SimulateContext = createContext<SimulateContextValue | null>(null);

export function SimulateProvider({ children }: PropsWithChildren) {
  const [datasets, setDatasets] = useState<SimulateDataset[]>([]);
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState<SimulateRun | null>(null);

  const addDatasets = useCallback((next: SimulateDataset[]) => {
    setDatasets((prev) => [...prev, ...next]);
  }, []);

  const removeDataset = useCallback((index: number) => {
    setDatasets((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearDatasets = useCallback(() => setDatasets([]), []);

  const startRun = useCallback(() => {
    if (datasets.length === 0) return;
    setRun({
      datasets,
      datasetIndex: 0,
      activeThreadId: null,
      phase: "need-thread",
      conversations: [],
    });
    setOpen(true);
  }, [datasets]);

  const beginConversation = useCallback((threadId: string, name?: string) => {
    setRun((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        activeThreadId: threadId,
        phase: "playing",
        conversations: [...prev.conversations, { threadId, name, turns: [] }],
      };
    });
  }, []);

  const recordTurn = useCallback((result: SimulateTurnResult) => {
    setRun((prev) => {
      if (!prev || prev.conversations.length === 0) return prev;
      const conversations = prev.conversations.slice();
      const last = conversations[conversations.length - 1]!;
      conversations[conversations.length - 1] = {
        ...last,
        turns: [...last.turns, result],
      };
      return { ...prev, conversations };
    });
  }, []);

  const finishConversation = useCallback(() => {
    setRun((prev) => {
      if (!prev || prev.phase !== "playing") return prev;
      const nextIndex = prev.datasetIndex + 1;
      if (nextIndex < prev.datasets.length) {
        return {
          ...prev,
          datasetIndex: nextIndex,
          activeThreadId: null,
          phase: "need-thread",
        };
      }
      return { ...prev, phase: "done", activeThreadId: null };
    });
  }, []);

  const reset = useCallback(() => setRun(null), []);

  const value = useMemo<SimulateContextValue>(
    () => ({
      datasets,
      addDatasets,
      removeDataset,
      clearDatasets,
      open,
      setOpen,
      run,
      startRun,
      beginConversation,
      recordTurn,
      finishConversation,
      reset,
    }),
    [
      datasets,
      addDatasets,
      removeDataset,
      clearDatasets,
      open,
      run,
      startRun,
      beginConversation,
      recordTurn,
      finishConversation,
      reset,
    ],
  );

  return (
    <SimulateContext.Provider value={value}>
      {children}
    </SimulateContext.Provider>
  );
}

export function useSimulate() {
  const context = useContext(SimulateContext);
  if (!context) {
    throw new Error("useSimulate must be used within a SimulateProvider");
  }
  return context;
}
